import { useParams, NavLink, Outlet } from 'react-router-dom';
import {
  Avatar,
  Box,
  Breadcrumbs,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useGetAMCQuery, useUpdateAMCMutation } from '../services/amcApi';
import { AMCProvider } from '../context/AMCContext';
import { useState } from 'react';
import { Edit } from '@mui/icons-material';
import { AMCForm } from '@/components/AMCForm';
import { Link as RouterLink } from 'react-router-dom';

const AMCDetail = () => {
  const { id = '' } = useParams<{ id: string }>();
  const { data: amc, isLoading: isLoadingAMC } = useGetAMCQuery(id);
  const [amcDetailsOpen, setAmcDetailsOpen] = useState(false);
  const [updateAMC] = useUpdateAMCMutation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const handleAmcDetailsClose = () => {
    setAmcDetailsOpen(false);
  };
  if (isLoadingAMC) {
    return <Typography>Loading...</Typography>;
  }

  if (!amc) {
    return <Typography>AMC not found</Typography>;
  }

  const handleUpdateAMC = async (values: typeof amc, logo: string) => {
    await updateAMC({
      updates: { ...values, ...(logo && { logo }) },
      id,
    }).unwrap();
  };

  return (
    <AMCProvider amcId={id} amc={amc}>
      <Box>
        <Breadcrumbs
          separator="·"
          sx={{
            mb: 1,
            '& .MuiBreadcrumbs-separator': {
              mx: 1, // Add some margin around the dot
              color: theme.palette.text.secondary, // Style the dot color
            },
          }}
        >
          <Link
            component={RouterLink}
            to="/dashboard"
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.subtitle1.fontSize,
              fontWeight: theme.typography.subtitle1.fontWeight,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Dashboard
          </Link>
          <Link
            component={RouterLink}
            to="/amcs"
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.subtitle1.fontSize,
              fontWeight: theme.typography.subtitle1.fontWeight,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Configuration
          </Link>
          <Typography variant="subtitle1">
            {window.location.pathname.includes('assets') ? 'Assets' : 'Payment Aggregators'}
          </Typography>
        </Breadcrumbs>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            paddingRight: '1em',
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              borderRadius: '7px',
              border: `1px solid ${theme.palette.divider}`,
              width: '34px',
              height: '34px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mr: 1,
            }}
          >
            {amc?.logoPath ? (
              <img src={amc?.logoPath} alt="AMC Logo" style={{ width: '27px', height: '27px' }} />
            ) : (
              <Avatar
                sx={{
                  borderRadius: '4px',
                  width: '27px',
                  height: '27px',
                }}
              >
                {amc.name.charAt(0).toUpperCase()}
              </Avatar>
            )}
          </Box>
          <div
            style={{
              marginBottom: 3,
              fontWeight: 'bold',
              fontSize: '23px',
              display: 'flex',
            }}
          >
            {amc.name}
          </div>
          <div
            style={{
              borderLeft: '1px solid black',
              marginLeft: '0.8em',
              paddingLeft: '0.4em',
              marginRight: '0.4em',
            }}
          >
            {amc.fundCode}
          </div>
          <IconButton size="medium" onClick={() => setAmcDetailsOpen(true)}>
            <Edit color="primary" fontSize="small" />
          </IconButton>
          {amc.isRTA && (
            <Typography
              variant="subtitle2"
              sx={{
                color: theme.palette.action.active,
                fontWeight: 'bold',
                fontSize: '0.875rem',
                ml: 1,
              }}
            >
              RTA
            </Typography>
          )}
        </div>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <NavLink
              to={`/amcs/${id}/assets`}
              style={({ isActive }) => ({
                textDecoration: 'none',
                padding: '12px 16px',
                color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                borderBottom: isActive ? `2px solid ${theme.palette.primary.main}` : 'none',
              })}
            >
              Assets
            </NavLink>
            <NavLink
              to={`/amcs/${id}/merchant-configs`}
              style={({ isActive }) => ({
                textDecoration: 'none',
                padding: '12px 16px',
                color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                borderBottom: isActive ? `2px solid ${theme.palette.primary.main}` : 'none',
              })}
            >
              Payment Aggregators
            </NavLink>
          </Box>
        </Box>
        <Outlet />
      </Box>
      <Dialog open={amcDetailsOpen} fullWidth onClose={handleAmcDetailsClose}>
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '23px' }}>Change Details</DialogTitle>
        <DialogContent>
          <div style={{ margin: '0.5em' }} />
          <AMCForm
            onSubmit={handleUpdateAMC as any}
            initialValues={amc}
            submitButtonText="Update"
          />
        </DialogContent>
      </Dialog>
    </AMCProvider>
  );
};

export default AMCDetail;
